import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

/**
 * Form-submission types (tasks 3.2/3.4, hardened 2.16 P-contract). The shared
 * contract file `contracts/form-submissions.contracts.ts` (task 2.9) is the
 * source of truth; these local interfaces mirror it for the Parent Portal build
 * (which does not import across the repo boundary). Keep in sync with that file.
 */

export type FormStatus = 'not_started' | 'draft' | 'pending_supervisor' | 'approved' | 'rejected';

export interface FormApplicability {
  readonly formId: number;
  readonly formKey: string;
  readonly formName: string;
  readonly requiresSupervisorSignature: boolean;
  readonly requiresInPersonSignature: boolean;
  readonly status: FormStatus;
  /** 2.16 P7: true once the parent's own signature exists. */
  readonly signedByParent: boolean;
}

export interface SignaturePayload {
  readonly method: 'drawn' | 'typed';
  /** A full `data:image/png;base64,` URI. */
  readonly signatureImage: string;
  /** Canonical typed-name field (2.16 A6). */
  readonly signatureTypedName?: string;
  readonly attestationSnapshot: string;
}

export interface EnrollmentChild {
  readonly firstName: string;
  readonly lastName: string;
  readonly fullName: string;
  /** Epoch seconds; 0 when unset. */
  readonly dateOfBirth: number;
}

export interface EnrollmentCentre {
  readonly id: number;
  readonly name: string;
}

export interface EnrollmentParent {
  readonly contactId: number;
  readonly name: string;
}

/**
 * rebuild-enrollment-intake 2.2 (DCM-363): the "confirm your details" gate of one
 * enrollment (server-authored; the parent only confirms or reports a problem).
 */
export interface EnrollmentDetails {
  readonly status: 'unconfirmed' | 'correction_open' | 'confirmed';
  readonly issueNote: string;
  readonly issueAt: number;
  readonly expectedStartDate: number;
}

/** GET /forms/enrollments/{enrollmentId} — the wizard's per-enrollment view. */
export interface EnrollmentForms {
  readonly enrollmentId: number;
  readonly childContactId: number;
  readonly status: string;
  readonly child: EnrollmentChild;
  readonly centre: EnrollmentCentre;
  readonly parents: ReadonlyArray<EnrollmentParent>;
  readonly forms: ReadonlyArray<FormApplicability>;
  /** 2.2: the details gate; absent on an older API (treated as confirmed). */
  readonly details?: EnrollmentDetails;
}

export interface FormQuestionType {
  readonly id: number;
  readonly formId: number;
  readonly name: string;
  readonly helpText: string;
  readonly optionsJson: string;
  readonly required: boolean;
  readonly sortOrder: number;
  readonly active: boolean;
  readonly controlType: 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'upload';
}

export interface FormAnswer {
  readonly id: number;
  readonly submissionId: number;
  readonly questionTypeId: number;
  readonly value: string;
}

export interface FormSignature {
  readonly id: number;
  readonly submissionId: number;
  readonly signerRole: 'parent' | 'supervisor' | 'doctor';
  readonly method: 'drawn' | 'typed' | 'authenticated_click';
  /** 4.3 (#87): the stored data-URI image (drawn signatures render from it). */
  readonly signatureImage?: string;
  readonly signatureTypedName: string;
  readonly signedDate: number;
}

export interface FormSubmissionResult {
  readonly id: number;
  readonly formId: number;
  readonly contactId: number;
  readonly status: FormStatus;
  readonly isCurrentLive: boolean;
  readonly enrollmentId: number;
  readonly versionNumber: number;
  readonly answers?: Readonly<Record<string, string>>;
}

export interface SignatureResult {
  readonly id: number;
  readonly submissionId: number;
  readonly submissionStatus: FormStatus;
}

/** GET /forms/children/{childId}/{formId} — the wizard step's full view. */
export interface ChildFormDetail {
  readonly formId: number;
  readonly formKey: string;
  readonly formName: string;
  readonly requiresSupervisorSignature: boolean;
  readonly requiresInPersonSignature: boolean;
  /** 2.16 A6: the server-authored attestation to show above the pad. */
  readonly attestation: string;
  readonly questions: ReadonlyArray<FormQuestionType>;
  readonly submission: FormSubmissionResult | null;
  readonly answers: ReadonlyArray<FormAnswer>;
  readonly releaseAnswer: { signerTypedName: string; signer2TypedName: string } | null;
  readonly signatures: ReadonlyArray<FormSignature>;
}

export interface FormSubmitRequest {
  readonly answers: Readonly<Record<string, string>>;
  readonly signerTypedName?: string;
  readonly signer2TypedName?: string;
  // 2.16 A5: no doctor's-note id here — it is attached via /attachments and enforced at /sign.
}

/**
 * 2.17 P1 (DCMPDEV-796): is one form "complete" for the parent's submit-to-centre?
 * An ONLINE form is complete once the parent has signed it (signedByParent); an
 * IN-PERSON form only needs its prefilled submission (a non-`not_started` status) —
 * its paper is signed at the centre. This is the single rule used by the wizard's
 * `allComplete` and the enrollment page's Submit gating.
 */
export function formComplete(f: FormApplicability): boolean {
  // 4.5 (#83, X4): a RETURNED form is never complete — for an online form the
  // old signature is still on file (signedByParent stays true), so status wins.
  if (f.status === 'rejected') return false;
  // In-person (review fix 2026-10-10): the prefill is the parent's part
  // (draft counts as answered); its paper is signed at the centre.
  if (f.requiresInPersonSignature) return f.status !== 'not_started';
  return f.signedByParent === true;
}

/** POST /forms/submissions/{id}/attachments — response (2.16 A10: no server path). */
export interface AttachmentResult {
  readonly id: number;
  /** The original upload file name. */
  readonly name: string;
  /** Size in bytes. */
  readonly size: number;
}

/** 2.10: one item still missing at submit time (server 422). */
export interface MissingFormItem {
  readonly formKey: string;
  readonly formName: string;
  /** 'missing' | 'unsigned' | 'doctor_note_missing' | 'not_approved' | 'not_scanned'. */
  readonly reason: string;
}

/** 2.10: the enrollment row returned by POST /forms/enrollments/{id}/submit. */
export interface SubmitEnrollmentResult {
  readonly id: number;
  readonly childContactId: number;
  readonly status: string;
  readonly formsSubmittedAt: number;
  readonly formsSubmittedBy: number;
  readonly returnedReason: string;
}

// ── add-enrollment-registration-and-consent-forms 3.1 (DCMPDEV-748) — Registration sections ──
// Mirrors gtlc-project/contracts/form-submissions.contracts.ts (written by task
// 2.1b) — the backend RegistrationSections.cfc shapes are authoritative.

/** The fixed Registration (enroll) section keys (RegistrationSections.sectionKeys). */
export type RegistrationSectionKey =
  | 'student' | 'school' | 'guardians' | 'emergency_contacts' | 'permissions' | 'doctor'
  | 'allergies' | 'food_restrictions' | 'medical_alerts' | 'special_alerts'
  | 'immunizations' | 'custody' | 'diaper_creams'
  // rebuild-enrollment-intake 4.0/5.2 (DCM-366): the Application's new sections.
  | 'diseases' | 'medical_needs' | 'health_notes' | 'feeding' | 'milkfish' | 'sleep'
  | 'toileting' | 'stroller' | 'handbook'
  // F4 (2.4, fix-enrollment-followups 2026-10-28): the optional photos step.
  | 'photos'
  // 8.5/8.6 (client meeting 2026-10-28, DCMPDEV-738 DCM-366): Physical needs
  // is its own section (old drafts answered it in health_notes); the lotion
  // list mirrors diaper_creams.
  | 'physical_needs' | 'moisturizing_lotions';

/** One staged section. Parents never receive the supervisor review fields. */
export interface RegistrationSectionPayload<T = unknown> {
  readonly section: RegistrationSectionKey;
  readonly payload: T;
  /** Epoch seconds. */
  readonly updatedAt: number;
  /**
   * 5.2: true when the GET returns the centre's known data for a section the
   * parent never saved (form-submissions.cfc prefilledSections) — display
   * prefill only, never a saved row.
   */
  readonly prefilled?: boolean;
}

/** GET forms/children/{childId}/enroll/sections — response (submissionId 0 = nothing saved yet). */
export interface RegistrationSections {
  readonly submissionId: number;
  /** False once submitted to the centre / once the enroll submission is past draft. */
  readonly editable: boolean;
  readonly sections: ReadonlyArray<RegistrationSectionPayload>;
  /** R50/R52 (2.6): the Infant age group's maximum in months (absent on an older API). */
  readonly infantMaxMonths?: number;
  /** 8.3 (M10): the milk/fish/eggs age ceiling in months (absent on an older API). */
  readonly milkfishMaxMonths?: number;
  /** R56 (2.6): the exact statement the parent signs (handbook clause only when one is current). */
  readonly statement?: string;
}

/** PUT forms/children/{childId}/enroll/sections/{section} — 200 response. */
export interface SaveRegistrationSectionResult<T = unknown> extends RegistrationSectionPayload<T> {
  readonly submissionId: number;
}

/** One field-path validation error in a section PUT 422 body. */
export interface SectionValidationError {
  /** e.g. 'student.dateOfBirth', 'allergies[1].description', 'custody.document_upload_id'. */
  readonly path: string;
  readonly message: string;
}

/**
 * 3.2/3.4 (DCMPDEV-683/699): form-submission endpoints for the registration
 * wizard, wired to the 2.3/2.8 backend endpoints. Extends ApiService per the
 * canonical pattern (user.service.ts).
 */
@Injectable({ providedIn: 'root' })
export class FormsService extends ApiService {
  /** 2.3: applicable forms + statuses + child/centre/parents for one enrollment. */
  getEnrollmentForms(enrollmentId: number): Observable<EnrollmentForms> {
    return this.get<EnrollmentForms>(`forms/enrollments/${enrollmentId}`);
  }

  /** 2.3: applicable forms + statuses for one child (list endpoint). */
  getApplicableForms(childId: number): Observable<FormApplicability[]> {
    return this.get<FormApplicability[]>(`forms/children/${childId}`);
  }

  /** 2.3: one form's question types + any draft for (child, form). */
  getForm(childId: number, formId: number): Observable<ChildFormDetail> {
    return this.get<ChildFormDetail>(`forms/children/${childId}/${formId}`);
  }

  /**
   * This change (diaper-cream step): save one typed Registration section —
   * PUT /forms/children/{childId}/enroll/sections/{section} with the section
   * payload (design.md Frontend Contract). The endpoint ships with task 2.1;
   * this call is written against that contract.
   */
  saveSection(childId: number, section: string, payload: unknown): Observable<SaveRegistrationSectionResult> {
    return this.saveRegistrationSection(childId, section as RegistrationSectionKey, payload);
  }

  /** 3.1 (DCMPDEV-748): the staged Registration sections for one child. */
  getRegistrationSections(childId: number): Observable<RegistrationSections> {
    return this.get<RegistrationSections>(`forms/children/${childId}/enroll/sections`);
  }

  /** 3.1 (DCMPDEV-748): save one Registration section (write call — no retry). */
  saveRegistrationSection(
    childId: number,
    section: RegistrationSectionKey,
    payload: unknown,
  ): Observable<SaveRegistrationSectionResult> {
    return this.put<SaveRegistrationSectionResult>(`forms/children/${childId}/enroll/sections/${section}`, {
      payload,
    });
  }

  /** 2.3: create a draft submission with the answers (staged until confirm-start). */
  submitForm(childId: number, formId: number, payload: FormSubmitRequest): Observable<FormSubmissionResult> {
    return this.post<FormSubmissionResult>(`forms/children/${childId}/${formId}/submit`, payload);
  }

  /** 2.3: sign a submission online (creates the FormSignatures row). */
  signSubmission(submissionId: number, signature: SignaturePayload): Observable<SignatureResult> {
    return this.post<SignatureResult>(`forms/submissions/${submissionId}/sign`, signature);
  }

  /** 2.8: doctor's-note attachment on a submission (PDF/JPEG/PNG/HEIC). */
  uploadAttachment(submissionId: number, file: File): Observable<AttachmentResult> {
    const body = new FormData();
    body.append('upload', file);
    body.append('fileName', file.name);
    // U1 (3.3 / DCMPDEV-767): no Content-Type override — buildOptions drops the
    // header for a FormData body so the browser sets the multipart boundary.
    return this.post<AttachmentResult>(`forms/submissions/${submissionId}/attachments`, body);
  }

  /** 2.10: parent "Submit to centre" — validates the set and moves the enrollment. */
  submitEnrollment(enrollmentId: number): Observable<SubmitEnrollmentResult> {
    return this.post<SubmitEnrollmentResult>(`forms/enrollments/${enrollmentId}/submit`, {});
  }

  /**
   * 2.2: POST forms/enrollments/{id}/details — `{ confirm: true }` accepts the
   * prefilled details, `{ issue: '...' }` reports a problem (409 while one is open).
   */
  confirmDetails(
    enrollmentId: number,
    body: { confirm: true } | { issue: string },
  ): Observable<EnrollmentDetails> {
    return this.post<EnrollmentDetails>(`forms/enrollments/${enrollmentId}/details`, body);
  }
}
